<template>
  <div class="ree-overlay" @click.self="$emit('close')">
    <div class="ree-modal">
      <div class="ree-header">
        <div>
          <h3 class="ree-title">Resend Volunteering Email</h3>
          <p class="ree-subtitle">{{ opportunityTitle }}</p>
        </div>
        <button type="button" class="ree-close" title="Close" @click="$emit('close')">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>

      <div class="ree-body">
        <p class="ree-help">
          Emails go to active students (AS) announcing this published volunteering opportunity.
        </p>

        <div class="ree-mode-list">
          <label class="ree-mode" :class="{ 'ree-mode--active': mode === 'now' }">
            <input v-model="mode" type="radio" value="now" class="sr-only" />
            <span class="ree-mode-radio"></span>
            <span>
              <span class="ree-mode-label">Send now</span>
              <span class="ree-mode-desc">Queue emails for immediate delivery</span>
            </span>
          </label>

          <label class="ree-mode" :class="{ 'ree-mode--active': mode === 'schedule' }">
            <input v-model="mode" type="radio" value="schedule" class="sr-only" />
            <span class="ree-mode-radio"></span>
            <span>
              <span class="ree-mode-label">Schedule</span>
              <span class="ree-mode-desc">Pick a future date and time (Asia/Dubai)</span>
            </span>
          </label>
        </div>

        <div v-if="mode === 'schedule'" class="ree-schedule">
          <label class="ree-field">
            <span>Date</span>
            <input v-model="scheduleDate" type="date" class="ree-input" :min="minDate" />
          </label>
          <label class="ree-field">
            <span>Time</span>
            <input v-model="scheduleTime" type="time" class="ree-input" />
          </label>
        </div>

        <p v-if="validationError" class="ree-error">{{ validationError }}</p>
      </div>

      <div class="ree-footer">
        <button type="button" class="ree-btn ree-btn--ghost" :disabled="isSubmitting" @click="$emit('close')">
          Cancel
        </button>
        <button type="button" class="ree-btn ree-btn--primary" :disabled="isSubmitting" @click="submit">
          <span v-if="isSubmitting">Queuing...</span>
          <span v-else>{{ mode === 'now' ? 'Send Now' : 'Schedule Send' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';

export default {
  name: 'ResendVolunteeringEmailModal',
  props: {
    opportunityToken: {
      type: String,
      required: true,
    },
    opportunityTitle: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'done'],
  data: function () {
    return {
      mode: 'now',
      scheduleDate: '',
      scheduleTime: '01:00',
      validationError: '',
      isSubmitting: false,
    };
  },
  computed: {
    minDate: function () {
      var now = new Date();
      var year = now.getFullYear();
      var month = now.getMonth() + 1;
      var day = now.getDate();
      var monthStr = month < 10 ? '0' + month : String(month);
      var dayStr = day < 10 ? '0' + day : String(day);
      return year + '-' + monthStr + '-' + dayStr;
    },
  },
  mounted: function () {
    this.scheduleDate = this.minDate;
  },
  methods: {
    normalizeScheduleTime: function () {
      if (!this.scheduleTime) {
        return '';
      }
      // <input type="time"> may be "HH:mm" or "HH:mm:ss"
      return this.scheduleTime.length === 5 ? this.scheduleTime + ':00' : this.scheduleTime;
    },
    buildScheduledAt: function () {
      if (!this.scheduleDate || !this.scheduleTime) {
        return null;
      }
      var time = this.normalizeScheduleTime();
      if (!time) {
        return null;
      }
      // Lighthouse DateTime scalar expects "Y-m-d H:i:s" (app timezone Asia/Dubai).
      return this.scheduleDate + ' ' + time;
    },
    scheduledAtAsDate: function () {
      var time = this.normalizeScheduleTime();
      if (!this.scheduleDate || !time) {
        return null;
      }
      var dubai = new Date(this.scheduleDate + 'T' + time + '+04:00');
      if (isNaN(dubai.getTime())) {
        return null;
      }
      return dubai;
    },
    submit: function () {
      var self = this;
      self.validationError = '';

      if (self.mode === 'schedule') {
        if (!self.scheduleDate || !self.scheduleTime) {
          self.validationError = 'Please select both date and time.';
          return;
        }
        var scheduledAt = self.buildScheduledAt();
        var scheduledDate = self.scheduledAtAsDate();
        if (!scheduledAt || !scheduledDate) {
          self.validationError = 'Invalid date or time.';
          return;
        }
        if (scheduledDate.getTime() <= Date.now()) {
          self.validationError = 'Scheduled time must be in the future.';
          return;
        }
      }

      self.isSubmitting = true;

      var variables = {
        token: self.opportunityToken,
        send_now: self.mode === 'now',
        scheduled_at: self.mode === 'schedule' ? self.buildScheduledAt() : null,
      };

      var MUTATION = gql`
        mutation ResendVolunteeringOpportunityEmail($token: String!, $send_now: Boolean, $scheduled_at: DateTime) {
          resendVolunteeringOpportunityEmail(token: $token, send_now: $send_now, scheduled_at: $scheduled_at) {
            error
            message
          }
        }
      `;

      self.$apollo
        .mutate({
          mutation: MUTATION,
          variables: variables,
        })
        .then(function (response) {
          self.isSubmitting = false;
          var payload =
            response && response.data && response.data.resendVolunteeringOpportunityEmail
              ? response.data.resendVolunteeringOpportunityEmail
              : null;

          if (!payload || payload.error) {
            self.validationError =
              payload && payload.message ? payload.message : 'Failed to queue emails.';
            return;
          }

          self.$emit('done', payload.message);
        })
        .catch(function (error) {
          self.isSubmitting = false;
          self.validationError =
            error && error.message ? error.message : 'Failed to queue emails.';
        });
    },
  },
};
</script>

<style scoped>
.ree-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.45);
}

.ree-modal {
  width: 100%;
  max-width: 28rem;
  background: #ffffff;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 40px -20px rgba(15, 23, 42, 0.45);
  overflow: hidden;
}

.ree-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.25rem 0.75rem;
}

.ree-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.ree-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.ree-close {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: none;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
}

.ree-body {
  padding: 0.75rem 1.25rem 1rem;
}

.ree-help {
  margin: 0 0 1rem;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #64748b;
}

.ree-mode-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.ree-mode {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  cursor: pointer;
  background: #ffffff;
}

.ree-mode--active {
  border-color: #2563eb;
  background: #eff6ff;
}

.ree-mode-radio {
  width: 1rem;
  height: 1rem;
  margin-top: 0.15rem;
  border-radius: 9999px;
  border: 2px solid #94a3b8;
  flex-shrink: 0;
  position: relative;
}

.ree-mode--active .ree-mode-radio {
  border-color: #2563eb;
}

.ree-mode--active .ree-mode-radio::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #2563eb;
}

.ree-mode-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
}

.ree-mode-desc {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: #64748b;
}

.ree-schedule {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
}

.ree-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
}

.ree-input {
  height: 2.4rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0 0.7rem;
  font-size: 0.85rem;
  color: #0f172a;
  outline: none;
}

.ree-input:focus {
  border-color: #2563eb;
}

.ree-error {
  margin: 0.85rem 0 0;
  font-size: 0.8rem;
  color: #dc2626;
}

.ree-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  padding: 0.9rem 1.25rem 1.2rem;
  border-top: 1px solid #eef2f7;
  background: #f8fafc;
}

.ree-btn {
  height: 2.35rem;
  padding: 0 1rem;
  border-radius: 0.55rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}

.ree-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ree-btn--ghost {
  background: #ffffff;
  border-color: #cbd5e1;
  color: #475569;
}

.ree-btn--primary {
  background: #1d4ed8;
  color: #ffffff;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
